import type { QueuePayload } from './queue-store'

/**
 * The content hash of a queued submission (M2-08, technical note).
 *
 * A submission that waits out a tunnel is captured at one moment and sent at another, and the whole
 * point of an offline queue is that nothing happens to it in between. So the bytes are hashed the
 * moment they are captured, the hash travels with the item, and the runner hashes the payload again
 * before it sends. A mismatch is never retried: it means the disk handed back something other than
 * what the driver signed for, and a signature is not something to guess at.
 *
 * The hash covers the *content*, never the transport. A reservation is hashed over what was typed
 * and over the hashes of its photos, not over the document ids the API assigns them — those do not
 * exist yet while the device is offline, and they are the server's receipt rather than the driver's
 * statement.
 */

/** How much of the hash the queue screen shows: enough to compare by eye, short enough to read. */
export const HASH_DISPLAY_LENGTH = 12

export async function contentHash(payload: QueuePayload): Promise<string> {
  switch (payload.kind) {
    case 'photo':
      return await sha256Hex(await payload.blob.arrayBuffer())

    case 'remark':
      return await sha256Text(
        canonicalJson({
          phase: payload.body.phase,
          type: payload.body.type,
          text: payload.body.text ?? null,
          photos: payload.photos.map((photo) => photo.hash),
        }),
      )

    case 'signature':
      return await sha256Text(canonicalJson(payload.body))
  }
}

/** The first characters of the hash, which is what a driver reads off a screen at a ramp. */
export function shortHash(hash: string): string {
  return hash.slice(0, HASH_DISPLAY_LENGTH)
}

export async function sha256Text(text: string): Promise<string> {
  return await sha256Hex(new TextEncoder().encode(text))
}

export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const source = bytes instanceof Uint8Array ? copyOf(bytes) : bytes
  const digest = await crypto.subtle.digest('SHA-256', source)
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * JSON with every object key in a fixed order, so the same content hashes the same whether it was
 * built by a form, read back from IndexedDB or rebuilt after a reload. `undefined` members are
 * dropped exactly as `JSON.stringify` drops them, which keeps "absent" and "absent" the same thing.
 */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(canonical(value))
}

function canonical(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonical)
  if (value === null || typeof value !== 'object') return value

  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, member]) => member !== undefined)
    .sort(([left], [right]) => left.localeCompare(right))

  return Object.fromEntries(entries.map(([key, member]) => [key, canonical(member)]))
}

/**
 * A plain `ArrayBuffer` of exactly these bytes. A `Uint8Array` may be a window onto a larger buffer
 * — which is what `TextEncoder` and a sliced blob hand back — and digesting the buffer would then
 * hash bytes that are not part of the payload.
 */
function copyOf(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.byteLength)
  new Uint8Array(copy).set(bytes)
  return copy
}
